import React from "react";
import AdminBanner from "../dashboard/AdminBanner";

const G = "#0077B5";

function fmtNum(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(n);
}

export default function DashboardPage({
  user, dashState, dashLoading,
  followerCount, followerInput, setFollowerInput, followerInputDirty, setFollowerInputDirty,
  followerSaved, setFollowerSaved, followerHistory, lastFollowerUpdate,
  handleSaveFollowers, handlePostingMode, handleGeneratePosts, postingMode,
  reviewWindowDay, reviewWindowTime, reviewWindowNextDate, reviewDeadline,
  generatingPosts, generateSecsLeft,
  onNavigateSettings, onNavigatePosts, onSetupPlug, getGreeting,
  dashTourStep, setDashTourStep,
}) {
  const impressionsPerPost = Math.max(1, Math.round((followerCount || 500) * 0.0022));
  const upcoming = Array.isArray(dashState?.upcoming_posts) ? dashState.upcoming_posts : [];
  // Filter to current week only for stat cards — upcoming may include next week's posts
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - daysSinceMonday);
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  const thisWeekPosts = upcoming.filter(p => {
    const iso = p.scheduled_iso || p.scheduled_time || "";
    if (!iso) return false;
    const d = new Date(iso);
    return d >= weekStart && d < weekEnd;
  });
  const postsThisWeek = thisWeekPosts.length;
  const weeklyReach = impressionsPerPost * Math.max(1, postsThisWeek);

  const pendingCount = thisWeekPosts.filter(p => p.status === "pending" || p.status === "rate_limited").length;
  const approvedCount = thisWeekPosts.filter(p => p.status === "approved" || p.status === "scheduled").length;
  const totalUpcoming = thisWeekPosts.length;

  return (
    <div style={{ margin: "-36px -24px -48px", background: "#f3f2ef" }}>
      {dashLoading && !dashState && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14 }}>Loading…</div>
      )}
      <div style={{ background: "#f3f2ef", minHeight: "100vh", padding: "16px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* TOP BAR — greeting + trial badge + review chip + mode toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 20, fontWeight: 500, color: "#191919" }}>
              {getGreeting()}, <span style={{ fontStyle: "italic", color: "#1D9E75" }}>{(user?.linkedin_name || user?.name || "").split(" ")[0]}</span>.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {dashState?.is_trialing && !dashState?.is_workspace_member && (
                <div style={{ background: "#E1F5EE", color: "#0F6E56", border: "1px solid #A8DCCA", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                  {dashState.trial_days_remaining ?? 29} days free · {{ free: "Free", presence: "Presence", authority: "Authority", team: "Team", agency: "Agency" }[dashState.plan] || "Authority"}
                </div>
              )}
              {dashState?.is_workspace_member && !dashState?.is_workspace_admin && (
                <div style={{ background: "#f8f7f5", color: "#888", border: "1px solid #e0ddd8", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 500, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                  Team Account
                </div>
              )}
              {/* Review by chip */}
              {postingMode === "review" && (() => {
                const chipLabel = reviewDeadline
                  ? (() => {
                      const d = new Date(reviewDeadline);
                      return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                    })()
                  : reviewWindowNextDate
                    ? (() => {
                        const d = new Date(reviewWindowNextDate + "T12:00:00");
                        return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                      })()
                    : `${reviewWindowDay} ${reviewWindowTime}`;
                return (
                  <button onClick={() => onNavigatePosts()} style={{ fontSize: 11, background: "#EEF6FF", color: "#0a66c2", padding: "3px 10px", borderRadius: 20, border: "1px solid #bdd6f5", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", whiteSpace: "nowrap", cursor: "pointer" }}>
                    Edit by {chipLabel}
                  </button>
                );
              })()}
              {/* Mode toggle */}
              <div style={{ display: "flex", border: "1px solid #e0dfdc", borderRadius: 8, overflow: "hidden", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                {[["review","Edit First"],["autopilot","Autopilot"]].map(([m,label]) => (
                  <button key={m} onClick={() => handlePostingMode(m)}
                    style={{ padding: "6px 14px", fontSize: 12, fontWeight: 500, border: "none", cursor: "pointer", background: postingMode === m ? "#0a66c2" : "transparent", color: postingMode === m ? "#fff" : "#666", transition: "all 0.15s" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {postingMode === "review" && (
            <div style={{ fontSize: 12, color: "#888", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginTop: -6 }}>
              Posts go out at their scheduled time · Reject or swap before then
            </div>
          )}

          {/* FOLLOWER BAR */}
          <div style={{ background: "#fff", border: "1px solid #e0dfdc", borderRadius: 12, padding: "12px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#191919", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>LinkedIn followers</span>
                <input
                  type="number"
                  value={followerInput}
                  onChange={e => { setFollowerInput(e.target.value); setFollowerInputDirty(true); setFollowerSaved(false); }}
                  style={{ width: 80, border: "1px solid #e0dfdc", borderRadius: 6, padding: "4px 8px", fontSize: 13, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", outline: "none" }}
                />
                {followerInputDirty && !followerSaved && (
                  <button onClick={handleSaveFollowers}
                    style={{ background: "#0a66c2", color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                    Save
                  </button>
                )}
                {followerSaved && (
                  <span style={{ fontSize: 12, color: "#1D9E75", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Saved</span>
                )}
              </div>
              <a href="https://www.linkedin.com/mynetwork/network-manager/people-follow/followers/" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: "#0a66c2", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", textDecoration: "none", fontWeight: 500 }}>
                View my followers
              </a>
            </div>
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
              <span style={{ fontSize: 12, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                Est. impressions per post: <strong>{fmtNum(impressionsPerPost)}</strong> · Est. weekly reach: <strong>{fmtNum(weeklyReach)}</strong>
              </span>
              <span style={{ fontSize: 11, color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                {lastFollowerUpdate
                  ? `Last updated ${new Date(lastFollowerUpdate).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}`
                  : "Default estimate · enter your real count to improve accuracy"}
              </span>
            </div>
            {followerHistory.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>History:</span>
                {followerHistory.map((h, i) => (
                  <span key={i} style={{ fontSize: 11, color: i === 0 ? "#1D9E75" : "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: i === 0 ? "#1D9E75" : "#ccc", display: "inline-block" }} />
                    {fmtNum(h.follower_count)} · {new Date(h.recorded_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                  </span>
                ))}
              </div>
            )}
          </div>

          <AdminBanner dashState={dashState} />

          {/* Plug setup banner — solo Authority+ users only (no workspace involvement) */}
          {(["authority","team","agency","authority_team","pro_team"].includes(dashState?.plan) || ["authority","team","agency","authority_team","pro_team"].includes(user?.plan)) && !dashState?.has_plug_configured && !user?.is_workspace_member && !user?.is_workspace_admin && onSetupPlug && (
            <div style={{ background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 10, padding: "14px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
              <span style={{ fontSize: 22 }}>&#127919;</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, color: "#92400E", fontWeight: 600 }}>Set up your weekly plug post</span>
                <span style={{ fontSize: 13, color: "#92400E", marginLeft: 8 }}>— one post a week, purely about you and what you offer.</span>
              </div>
              <button onClick={onSetupPlug} style={{ background: "#D97706", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", minHeight: 36 }}>Set it up</button>
            </div>
          )}

          {/* Subscribe banner — trialing users */}
          {dashState?.is_trialing && !user?.is_workspace_member && onNavigateSettings && (
            <div style={{ background: "#EBF5FB", border: "1px solid #B3D4E8", borderRadius: 10, padding: "14px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
              <span style={{ fontSize: 20 }}>&#128274;</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, color: "#005885", fontWeight: 600 }}>Lock in your plan</span>
                <span style={{ fontSize: 13, color: "#005885", marginLeft: 8 }}>— subscribe to keep your features before your trial ends.</span>
              </div>
              <button onClick={() => onNavigateSettings("billing")} style={{ background: "#0077B5", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", minHeight: 36 }}>Subscribe now</button>
            </div>
          )}

          {/* 4 STAT CARDS */}
          <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
            {[
              { label: "Posts this week", value: postsThisWeek, sub: "scheduled" },
              { label: "Est. reach this week", value: fmtNum(weeklyReach), sub: "impressions" },
              { label: "Total posts sent", value: dashState?.total_posts_sent || 0, sub: "all time" },
              { label: "Total est. impressions", value: fmtNum((dashState?.total_posts_sent || 0) * impressionsPerPost), sub: "all time" },
            ].map(s => (
              <div key={s.label} style={{ background: "#fff", border: "1px solid #e0dfdc", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: "#191919", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginTop: 2 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* POST REVIEW SUMMARY CARD */}
          {totalUpcoming > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e0dfdc", borderRadius: 12, padding: "20px 24px" }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#191919", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 8 }}>
                You have {totalUpcoming} post{totalUpcoming !== 1 ? "s" : ""} this week
              </div>
              <div style={{ fontSize: 13, color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginBottom: 16 }}>
                {pendingCount > 0 && <span style={{ color: "#854F0B" }}>{pendingCount} pending</span>}
                {pendingCount > 0 && approvedCount > 0 && " · "}
                {approvedCount > 0 && <span style={{ color: "#0F6E56" }}>{approvedCount} approved</span>}
              </div>
              <button
                onClick={onNavigatePosts}
                data-testid="btn-home-review-posts"
                style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "10px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                Review posts →
              </button>
            </div>
          )}

          {/* GENERATE NOW — only shown when no posts exist */}
          {!generatingPosts && totalUpcoming === 0 && (
            <div style={{ background: "#fff", border: "1px solid #e0dfdc", borderRadius: 12, padding: "20px 24px", display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ flex: 1, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#191919", marginBottom: 2 }}>No posts queued yet</div>
                <div style={{ fontSize: 13, color: "#666" }}>Generate your posts for this week right now.</div>
              </div>
              <button onClick={handleGeneratePosts}
                style={{ background: "#0a66c2", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
                Generate now →
              </button>
            </div>
          )}

          {/* GENERATING SPINNER */}
          {generatingPosts && (
            <div style={{ background: "#fff", border: "1px solid #e0dfdc", borderRadius: 12, padding: "32px", textAlign: "center", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>🐝</div>
              <div style={{ fontSize: 14, color: "#0a66c2", fontWeight: 600, marginBottom: 4 }}>Your bees are writing your posts…</div>
              <div style={{ fontSize: 13, color: "#888" }}>
                {generateSecsLeft > 0 ? `Ready in about ${generateSecsLeft}s` : "Almost done…"}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Tour replay button */}
      {dashTourStep < 0 && (
        <button onClick={() => { setDashTourStep(0); localStorage.removeItem("nectar_tour_completed"); }}
          style={{ position: "fixed", bottom: 24, right: 24, background: "#fff", color: "#666", border: "1px solid #e0dfdc", borderRadius: 20, padding: "8px 16px", fontSize: 12, cursor: "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", zIndex: 50 }}>
          Take the tour
        </button>
      )}
    </div>
  );
}
